import { createAction, props } from '@ngrx/store';

import { MarketAnalysisResult } from '../../../drugs/models/drug-analysis.model';
import { LinkKey, LinkStateUpdate } from '../../../linking/models/link-pair.model';
import { CompetitorChartData, CompetitorSubMarket } from '../../models/competitor-chart-data.model';
import { Competitor } from '../../models/competitor.model';
import { UpdateHospitalProductAnalysisSettings } from '../../models/hospital-product-analysis-settings.model';
import { PriceDevelopmentChartData } from '../../models/price-development-chart-data.model';
import {
  ProductAnalysisSettings,
  UpdateProductAnalysisSettings,
} from '../../models/product-analysis-settings.model';
import { Product } from '../../models/product.model';
import { PurchaseProduct } from '../../models/purchase-product.model';

export const loadCompetitors: ReturnType<typeof createAction<string, { productId: string }>> =
  createAction('[ProductOverview Competitors] Load Competitors', props<{ productId: string }>());

export const loadCompetitorsSuccess: ReturnType<
  typeof createAction<string, { competitors: Competitor[] }>
> = createAction(
  '[ProductOverview Competitors] Load Competitors Success',
  props<{ competitors: Competitor[] }>()
);

export const loadCompetitorsFailure: ReturnType<typeof createAction<string, { error: string }>> =
  createAction(
    '[ProductOverview Competitors] Load Competitors Failure',
    props<{ error: string }>()
  );

export const loadPriceDevelopment: ReturnType<typeof createAction<string, { productId: string }>> =
  createAction(
    '[ProductOverview PriceDevelopment] Load Price Development',
    props<{ productId: string }>()
  );

export const loadPriceDevelopmentSuccess: ReturnType<
  typeof createAction<string, { data: PriceDevelopmentChartData }>
> = createAction(
  '[ProductOverview PriceDevelopment] Load Price Development Success',
  props<{ data: PriceDevelopmentChartData }>()
);

export const loadPriceDevelopmentFailure: ReturnType<
  typeof createAction<string, { error: string }>
> = createAction(
  '[ProductOverview PriceDevelopment] Load Price Development Failure',
  props<{ error: string }>()
);

export const loadSalesDevelopment: ReturnType<
  typeof createAction<string, { productId: string; subMarket: CompetitorSubMarket }>
> = createAction(
  '[ProductOverview SalesDevelopment] Load Sales Development',
  props<{ productId: string; subMarket: CompetitorSubMarket }>()
);

export const loadSalesDevelopmentSuccess: ReturnType<
  typeof createAction<string, { subMarket: CompetitorSubMarket; data: CompetitorChartData }>
> = createAction(
  '[ProductOverview SalesDevelopment] Load Sales Development Success',
  props<{ subMarket: CompetitorSubMarket; data: CompetitorChartData }>()
);

export const loadSalesDevelopmentFailure: ReturnType<
  typeof createAction<string, { subMarket: CompetitorSubMarket; error: string }>
> = createAction(
  '[ProductOverview SalesDevelopment] Load Sales Development Failure',
  props<{ subMarket: CompetitorSubMarket; error: string }>()
);

export const setChartPeriod: ReturnType<typeof createAction<string, { periodMonths: number }>> =
  createAction('[ProductOverview Charts] Set Chart Period', props<{ periodMonths: number }>());

export const loadIpgSummary: ReturnType<typeof createAction<string, { productId: string }>> =
  createAction('[ProductOverview IpgSummary] Load IPG Summary', props<{ productId: string }>());

export const loadIpgSummarySuccess: ReturnType<
  typeof createAction<string, { results: MarketAnalysisResult[] }>
> = createAction(
  '[ProductOverview IpgSummary] Load IPG Summary Success',
  props<{ results: MarketAnalysisResult[] }>()
);

export const loadIpgSummaryFailure: ReturnType<typeof createAction<string, { error: string }>> =
  createAction('[ProductOverview IpgSummary] Load IPG Summary Failure', props<{ error: string }>());

export const loadPurchaseProducts: ReturnType<
  typeof createAction<string, { productId: string; subMarket: CompetitorSubMarket }>
> = createAction(
  '[ProductOverview PurchaseProducts] Load Purchase Products',
  props<{ productId: string; subMarket: CompetitorSubMarket }>()
);

export const loadPurchaseProductsSuccess: ReturnType<
  typeof createAction<string, { subMarket: CompetitorSubMarket; products: PurchaseProduct[] }>
> = createAction(
  '[ProductOverview PurchaseProducts] Load Purchase Products Success',
  props<{ subMarket: CompetitorSubMarket; products: PurchaseProduct[] }>()
);

export const loadPurchaseProductsFailure: ReturnType<
  typeof createAction<string, { subMarket: CompetitorSubMarket; error: string }>
> = createAction(
  '[ProductOverview PurchaseProducts] Load Purchase Products Failure',
  props<{ subMarket: CompetitorSubMarket; error: string }>()
);

export const loadProduct: ReturnType<typeof createAction<string, { productId: string }>> =
  createAction('[ProductOverview Product] Load Product', props<{ productId: string }>());

export const loadProductSuccess: ReturnType<typeof createAction<string, { product: Product }>> =
  createAction('[ProductOverview Product] Load Product Success', props<{ product: Product }>());

export const loadProductFailure: ReturnType<typeof createAction<string, { error: string }>> =
  createAction('[ProductOverview Product] Load Product Failure', props<{ error: string }>());

export const loadAnalysisSettings: ReturnType<typeof createAction<string, { productId: string }>> =
  createAction(
    '[ProductOverview AnalysisSettings] Load Analysis Settings',
    props<{ productId: string }>()
  );

export const loadAnalysisSettingsSuccess: ReturnType<
  typeof createAction<string, { settings: ProductAnalysisSettings }>
> = createAction(
  '[ProductOverview AnalysisSettings] Load Analysis Settings Success',
  props<{ settings: ProductAnalysisSettings }>()
);

export const loadAnalysisSettingsFailure: ReturnType<
  typeof createAction<string, { error: string }>
> = createAction(
  '[ProductOverview AnalysisSettings] Load Analysis Settings Failure',
  props<{ error: string }>()
);

export const saveAnalysisSettings: ReturnType<
  typeof createAction<string, { settings: UpdateProductAnalysisSettings }>
> = createAction(
  '[ProductOverview AnalysisSettings] Save Analysis Settings',
  props<{ settings: UpdateProductAnalysisSettings }>()
);

export const saveAnalysisSettingsSuccess: ReturnType<
  typeof createAction<string, { productId: string }>
> = createAction(
  '[ProductOverview AnalysisSettings] Save Analysis Settings Success',
  props<{ productId: string }>()
);

export const saveAnalysisSettingsFailure: ReturnType<
  typeof createAction<string, { error: string }>
> = createAction(
  '[ProductOverview AnalysisSettings] Save Analysis Settings Failure',
  props<{ error: string }>()
);

export const saveHospitalSettings: ReturnType<
  typeof createAction<string, { settings: UpdateHospitalProductAnalysisSettings }>
> = createAction(
  '[ProductOverview HospitalSettings] Save Hospital Settings',
  props<{ settings: UpdateHospitalProductAnalysisSettings }>()
);

export const saveHospitalSettingsSuccess: ReturnType<
  typeof createAction<string, { productId: string }>
> = createAction(
  '[ProductOverview HospitalSettings] Save Hospital Settings Success',
  props<{ productId: string }>()
);

export const saveHospitalSettingsFailure: ReturnType<
  typeof createAction<string, { error: string }>
> = createAction(
  '[ProductOverview HospitalSettings] Save Hospital Settings Failure',
  props<{ error: string }>()
);

export const changePurchaseProductLinks: ReturnType<
  typeof createAction<string, { productId: string; updates: LinkStateUpdate[]; deletes: LinkKey[] }>
> = createAction(
  '[ProductOverview PurchaseProducts] Change Purchase Product Links',
  props<{ productId: string; updates: LinkStateUpdate[]; deletes: LinkKey[] }>()
);

export const changePurchaseProductLinksSuccess: ReturnType<
  typeof createAction<string, { productId: string }>
> = createAction(
  '[ProductOverview PurchaseProducts] Change Purchase Product Links Success',
  props<{ productId: string }>()
);

export const changePurchaseProductLinksFailure: ReturnType<
  typeof createAction<string, { productId: string; error: string }>
> = createAction(
  '[ProductOverview PurchaseProducts] Change Purchase Product Links Failure',
  props<{ productId: string; error: string }>()
);

export const setPurchaseProductNotesCount: ReturnType<
  typeof createAction<string, { productId: string; notesCount: number }>
> = createAction(
  '[ProductOverview PurchaseProducts] Set Purchase Product Notes Count',
  props<{ productId: string; notesCount: number }>()
);
